class GSPComplementaryBundle extends HTMLElement {
  constructor() {
    super();

    this._loaded = false;
    this._observer = null;
    this.unsubscribeVariantChange = null;
    this.imagePreloadCache = new Set();
    this._updateScheduled = false;
  }

  connectedCallback() {
    if (this.hasAttribute("data-bundle-ready")) {
      this.showBundleSection();
      this.initBundle();
      return;
    }

    this.loadRecommendations();
  }

  disconnectedCallback() {
    if (this.unsubscribeVariantChange) {
      this.unsubscribeVariantChange();
      this.unsubscribeVariantChange = null;
    }
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  initObserver() {
    if (!("IntersectionObserver" in window)) {
      this.loadRecommendations();
      return;
    }

    this._observer = new IntersectionObserver((entries) => {
      if (!entries[0] || !entries[0].isIntersecting) return;

      this._observer.disconnect();
      this._observer = null;
      this.loadRecommendations();
    });

    this._observer.observe(this);
  }

  loadRecommendations() {
    if (this._loaded) return;
    this._loaded = true;

    const url = this.dataset.url;
    if (!url) {
      this.hideBundleSection();
      return;
    }

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;

        const newBundle = html.querySelector("gsp-complementary-bundle");
        if (!newBundle) {
          this.hideBundleSection();
          return;
        }

        const hasRecommendations = newBundle.dataset.hasRecommendations === "true";

        if (!hasRecommendations) {
          this.hideBundleSection();
          return;
        }

        this.innerHTML = newBundle.innerHTML;
        this.setAttribute("data-bundle-ready", "true");
        this.showBundleSection();
        this.initBundle();
      })
      .catch((error) => {
        console.error("Complementary bundle load error:", error);
        this.hideBundleSection();
      });
  }

  getBundleContainer() {
    return this.closest("[data-bundle-container]") || this.closest(".gsp-complementary-bundle");
  }

  showBundleSection() {
    const wrapper = this.getBundleContainer();
    if (!wrapper) return;

    wrapper.classList.remove("is-pending");
    wrapper.style.display = "";
  }

  hideBundleSection() {
    const wrapper = this.getBundleContainer();

    if (wrapper) {
      wrapper.classList.remove("is-pending");
      wrapper.style.display = "none";
      return;
    }

    this.style.display = "none";
  }

  initBundle() {
    this.items = Array.from(this.querySelectorAll("[data-bundle-item]"));
    this.checkboxes = Array.from(this.querySelectorAll("[data-bundle-checkbox]"));
    this.variantOptions = Array.from(this.querySelectorAll("[data-bundle-variant-option]"));
    this.subtotalElement = this.querySelector("[data-bundle-subtotal]");
    this.addButton = this.querySelector("[data-bundle-add]");
    this.addButtonContent = this.addButton?.querySelector(".gsp-button__content") || null;
    this.addButtonSpin = this.addButton?.querySelector(".gsp-button__spin") || null;

    this.cart =
      document.querySelector("gsp-cart-drawer") ||
      document.querySelector("gsp-cart") ||
      null;

    this.cartItems = document.querySelector("gsp-cart-drawer-items") || null;
    this.enableCartDrawer = window?.gspSetting?.shop?.enable_add_to_cart_drawer || false;

    if (!this.items.length) return;

    this.baseButtonLabel = this.dataset.buttonLabel || window.gspString?.addToCart || "Add to cart";

    this.bindEvents();
    this.bindVariantEvents();
    this.preloadVariantImages();
    this.updateSubtotal();
    this.updateButtonLabel();
    this.setButtonLoading(false);
  }

  bindEvents() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => this.scheduleUpdate());
    });

    this.variantOptions.forEach((button) => {
      button.addEventListener("click", (event) => {
        this.handleBundleVariantOptionClick(event.currentTarget);
      });
    });

    this.addButton?.addEventListener("click", () => this.handleAddToCart());
  }

  bindVariantEvents() {
    const productId = this.dataset.productId;
    if (!productId) return;
    if (!window.gspEvents || typeof window.gspEvents.subscribe !== "function") return;

    if (this.unsubscribeVariantChange) {
      this.unsubscribeVariantChange();
      this.unsubscribeVariantChange = null;
    }

    this.unsubscribeVariantChange = window.gspEvents.subscribe(
      `${productId}__VARIANT_CHANGE`,
      (variant) => this.handleVariantChangeEvent(variant)
    );
  }

  preloadVariantImages() {
    this.variantOptions.forEach((button) => {
      const src = button.dataset.imageSrc;
      if (!src || this.imagePreloadCache.has(src)) return;

      const img = new Image();
      img.decoding = "async";
      img.src = src;
      this.imagePreloadCache.add(src);
    });
  }

  scheduleUpdate() {
    if (this._updateScheduled) return;
    this._updateScheduled = true;

    requestAnimationFrame(() => {
      this.updateSubtotal();
      this.updateButtonLabel();
      this._updateScheduled = false;
    });
  }

  handleVariantChangeEvent(variant) {
    const currentItem = this.querySelector('[data-bundle-item][data-current-product="true"]');
    if (!currentItem || !variant?.id) return;

    this.applyVariantToItem(currentItem, {
      variantId: variant.id,
      price: variant.price || 0,
      comparePrice: variant.compare_at_price || 0
    });

    this.scheduleUpdate();
  }

  handleBundleVariantOptionClick(button) {
    if (!button || button.disabled) return;

    const item = button.closest("[data-bundle-item]");
    if (!item) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.setActiveVariantOption(item, button);

    this.applyVariantToItem(item, {
      variantId,
      price: parseInt(button.dataset.price || "0", 10),
      comparePrice: parseInt(button.dataset.comparePrice || "0", 10)
    });

    this.updateImageUI(item, {
      src: button.dataset.imageSrc || "",
      alt: button.dataset.imageAlt || ""
    });

    this.scheduleUpdate();
  }

  setActiveVariantOption(item, activeButton) {
    const buttons = item.querySelectorAll("[data-bundle-variant-option]");

    buttons.forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  }

  applyVariantToItem(item, { variantId, price, comparePrice = 0 }) {
    item.dataset.variantId = String(variantId);
    item.dataset.price = String(price);
    this.updatePriceUI(item, price, comparePrice);
  }

  updatePriceUI(item, price, comparePrice = 0) {
    const currentPriceElement = item.querySelector("[data-bundle-item-price]");
    if (currentPriceElement) {
      currentPriceElement.textContent = this.formatMoney(price);
    }

    const priceWrap = item.querySelector("[data-bundle-item-price-wrap]");
    if (!priceWrap) return;

    let comparePriceElement = item.querySelector("[data-bundle-item-compare-price]");

    if (comparePrice > price) {
      if (!comparePriceElement) {
        comparePriceElement = document.createElement("span");
        comparePriceElement.className = "gsp-complementary-bundle__price-compare";
        comparePriceElement.setAttribute("data-bundle-item-compare-price", "");
        priceWrap.appendChild(comparePriceElement);
      }

      comparePriceElement.textContent = this.formatMoney(comparePrice);
    } else {
      comparePriceElement?.remove();
    }
  }

  updateImageUI(item, { src = "", alt = "" }) {
    if (!src) return;

    const imageWrap = item.querySelector("[data-bundle-item-image-wrap]");
    if (!imageWrap) return;

    const image = imageWrap.querySelector("img");
    if (!image) return;
    if (image.dataset.currentSrc === src) return;

    imageWrap.classList.add("is-image-loading");

    const preloadImage = new Image();
    preloadImage.decoding = "async";

    preloadImage.onload = () => {
      image.src = src;
      image.removeAttribute("srcset");

      if (alt) {
        image.alt = alt;
      }

      image.dataset.currentSrc = src;

      requestAnimationFrame(() => {
        imageWrap.classList.remove("is-image-loading");
      });
    };

    preloadImage.onerror = () => {
      imageWrap.classList.remove("is-image-loading");
      console.warn("Failed to load variant image:", src);
    };

    preloadImage.src = src;
  }

  getSelectedItems() {
    return this.items.filter((item) => {
      const checkbox = item.querySelector("[data-bundle-checkbox]");
      return checkbox?.checked;
    });
  }

  getSelectedLineItems() {
    return this.getSelectedItems()
      .map((item) => {
        const variantId = item.dataset.variantId;
        if (!variantId) return null;

        return {
          id: parseInt(variantId, 10),
          quantity: 1
        };
      })
      .filter(Boolean);
  }

  updateSubtotal() {
    if (!this.subtotalElement) return;

    const total = this.getSelectedItems().reduce((sum, item) => {
      return sum + parseInt(item.dataset.price || "0", 10);
    }, 0);

    this.subtotalElement.textContent = this.formatMoney(total);
  }

  updateButtonLabel() {
    if (!this.addButtonContent) return;

    const count = this.getSelectedItems().length;

    if (count <= 0) {
      this.addButtonContent.textContent = this.baseButtonLabel;
      return;
    }

    this.addButtonContent.textContent = `Add ${count} item${count > 1 ? "s" : ""} to cart`;
  }

  async handleAddToCart() {
    const items = this.getSelectedLineItems();
    if (!items.length) return;

    this.setButtonLoading(true);

    try {
      const root = window?.Shopify?.routes?.root || "/";
      const addUrl = `${root}cart/add.js`.replace(/\/{2,}/g, "/");
      const cartUrl = `${root}cart.js`.replace(/\/{2,}/g, "/");

      const payload = { items };

      if (this.cart && typeof this.cart.getSectionsToRender === "function") {
        payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const addResponse = await fetch(addUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      }).then(async (response) => {
        if (!response.ok) {
          throw await response.json();
        }
        return response.json();
      });

      if (
        this.enableCartDrawer &&
        this.cart &&
        typeof this.cart.renderContents === "function"
      ) {
        this.cart.renderContents(addResponse);
      }

      const cartData = await fetch(cartUrl, {
        method: "GET",
        headers: {
          "Accept": "application/json"
        }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }
        return response.json();
      });

      if (addResponse.sections) {
        cartData.sections = addResponse.sections;
      }

      this.handleCartSuccess(cartData);
    } catch (error) {
      this.handleCartError(error);
    } finally {
      this.setButtonLoading(false);
    }
  }

  handleCartSuccess(cartData) {
    if (window.gspEvents && typeof window.gspEvents.emit === "function") {
      window.gspEvents.emit("cart-update", cartData);
    }

    if (
      window.gspUpdateCartCountInHeader &&
      typeof window.gspUpdateCartCountInHeader.update === "function"
    ) {
      window.gspUpdateCartCountInHeader.update();
    }

    if (window.gspNotification) {
      window.gspNotification.show({
        target: this.enableCartDrawer ? this.cartItems : document.body,
        message: window?.gspString?.itemAdded || "Item added to cart",
        type: "success",
        method: "appendChild",
        icon: "circle_check",
        sticky: !this.enableCartDrawer
      });
    }

    if (this.cart?.classList.contains("gsp-cart-empty")) {
      this.cart.classList.remove("gsp-cart-empty");
    }
  }

  handleCartError(error) {
    console.error("Complementary bundle add to cart error:", error);

    if (window.gspNotification) {
      window.gspNotification.show({
        message:
          error?.description ||
          error?.message ||
          "Unable to add bundle items to cart",
        type: "danger",
        method: "appendChild",
        icon: "circle_xmark",
        sticky: true
      });
    }
  }

  setButtonLoading(isLoading) {
    if (!this.addButton) return;

    this.addButton.disabled = isLoading;
    this.addButton.classList.toggle("is-loading", isLoading);
    this.addButtonContent?.classList.toggle("active", !isLoading);
    this.addButtonSpin?.classList.toggle("active", isLoading);
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const format =
        window?.gspSetting?.money_format ||
        window?.Shopify?.money_format ||
        "${{amount}}";

      return window.Shopify.formatMoney(cents, format);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get("gsp-complementary-bundle")) {
  customElements.define("gsp-complementary-bundle", GSPComplementaryBundle);
}